import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { ProductConfig } from '../types/product';
import { Product } from '../types/product';
import { API_BASE_URL } from './baseQuery';

export interface ProductVariant {
  id: string;
  productId: string;
  material: string;
  color: string;
  size: string;
  price: number;
  stock: number;
  type?: string;
  images?: string[];
  specifications?: Record<string, any>;
}

export const productDisplayApi = createApi({
  reducerPath: 'productDisplayApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${API_BASE_URL}/public`,
    credentials: 'include',
  }),
  endpoints: (builder) => ({
    getProducts: builder.query<Product[], void>({
      query: () => 'products',
    }),
    getProductById: builder.query<Product, string>({
      query: (id) => `products/${id}`,
    }),
    getCategories: builder.query<string[], void>({
      query: () => 'products/categories',
    }),
    getProductVariants: builder.query<ProductVariant[], string>({
      query: (productId) => `products/${productId}/variants`,
    }),
    getProduct: builder.query<Product, string>({
      query: (productId) => `products/${productId}`,
    }),
    updateProductVariant: builder.mutation<void, { productId: string; variantId: string; updates: Partial<ProductVariant> }>({
      query: ({ productId, variantId, updates }) => ({
        url: `products/${productId}/variants/${variantId}`,
        method: 'PUT',
        body: updates,
      }),
    }),
    saveProductConfig: builder.mutation<void, { productId: string; config: ProductConfig }>({
      query: ({ productId, config }) => ({
        url: `products/${productId}/config`,
        method: 'POST',
        body: config,
      }),
      transformResponse: (response: { success: boolean }) => {
        if (!response.success) {
          throw new Error('Failed to save product configuration');
        }
        return undefined;
      },
    }),
    getMaterials: builder.query<string[], string>({
      query: (type) => `materials/${type}`,
      transformResponse: (response: { data: string[] }) => response.data,
    }),
    getColors: builder.query<string[], string>({
      query: (type) => `colors/${type}`,
      transformResponse: (response: { data: string[] }) => response.data,
    }),
  }),
});

export const { 
  useGetProductsQuery, 
  useGetProductByIdQuery,
  useGetCategoriesQuery,
  useGetProductVariantsQuery,
  useSaveProductConfigMutation,
} = productDisplayApi;
